import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Tab, Tabs, Typography, Paper, useTheme } from '@mui/material';
import { TableRestaurant, LocalShipping } from '@mui/icons-material';
import CardLayoutTheme from '../theme/CardLayoutTheme';
import TableOrdersTab from '../components/invoices/TableOrdersTab';
import OtherOrdersTab from '../components/invoices/OtherOrdersTab';
import { DateRange, getDateRangeForPreset, DatePreset } from '../components/invoices/DateRangeSelector';

// Local storage keys for session persistence (FR-002)
const STORAGE_KEY_TAB = 'vendor_invoices_selected_tab';
const STORAGE_KEY_DATE_PRESET = 'vendor_invoices_date_preset';
const STORAGE_KEY_OTHER_DATE_FILTER = 'vendor_invoices_other_date_filter';

type TabValue = 'table-orders' | 'other-orders';

interface TabPanelProps {
  children?: React.ReactNode;
  value: TabValue;
  currentTab: TabValue;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, currentTab }) => {
  return (
    <Box
      role="tabpanel"
      hidden={value !== currentTab}
      sx={{ pt: 3 }}
    >
      {value === currentTab && children}
    </Box>
  );
};

const VendorInvoices: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const theme = useTheme();

  // Initialize state from localStorage for session persistence (FR-002)
  const [selectedTab, setSelectedTab] = useState<TabValue>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_TAB);
    return (saved === 'table-orders' || saved === 'other-orders') ? saved : 'table-orders';
  });

  const [tableOrdersDateRange, setTableOrdersDateRange] = useState<DateRange>(() => {
    const savedPreset = localStorage.getItem(STORAGE_KEY_DATE_PRESET) as DatePreset;
    if (savedPreset && ['today', 'yesterday', 'week', 'month', 'custom'].includes(savedPreset)) {
      return getDateRangeForPreset(savedPreset);
    }
    return getDateRangeForPreset('today');
  });

  const [otherOrdersDateFilter, setOtherOrdersDateFilter] = useState<string>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_OTHER_DATE_FILTER);
    return saved || 'today';
  });

  // Persist selected tab to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_TAB, selectedTab);
  }, [selectedTab]);

  // Persist date preset to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_DATE_PRESET, tableOrdersDateRange.preset);
  }, [tableOrdersDateRange.preset]);

  // Persist other orders date filter to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_OTHER_DATE_FILTER, otherOrdersDateFilter);
  }, [otherOrdersDateFilter]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: TabValue) => {
    setSelectedTab(newValue);
  };

  const handleTableOrdersDateRangeChange = useCallback((range: DateRange) => {
    setTableOrdersDateRange(range);
  }, []);

  const handleOtherOrdersDateFilterChange = useCallback((filter: string) => {
    setOtherOrdersDateFilter(filter);
  }, []);

  if (!vendorId) {
    return (
      <CardLayoutTheme
        title="Invoice Management"
        categories={[{ id: 1, name: 'All Invoices' }]}
        renderItems={() => (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error">Vendor ID is missing. Cannot load invoices.</Typography>
          </Box>
        )}
        hideSidebar={true}
      />
    );
  }

  return (
    <CardLayoutTheme
      title="Invoice Management"
      categories={[{ id: 1, name: 'All Invoices' }]}
      renderItems={() => (
        <Box>
          {/* Tab Navigation */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              mb: 2,
              backgroundColor: theme.palette.background.paper
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  py: 1.5
                },
                '& .Mui-selected': {
                  fontWeight: 600
                }
              }}
            >
              <Tab
                value="table-orders"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TableRestaurant sx={{ fontSize: 20 }} />
                    <span>Table Orders</span>
                  </Box>
                }
              />
              <Tab
                value="other-orders"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShipping sx={{ fontSize: 20 }} />
                    <span>Other Orders</span>
                  </Box>
                }
              />
            </Tabs>
          </Paper>

          {/* Tab Panels */}
          <TabPanel value="table-orders" currentTab={selectedTab}>
            <TableOrdersTab
              onDateRangeChange={handleTableOrdersDateRangeChange}
              initialDateRange={tableOrdersDateRange}
            />
          </TabPanel>

          <TabPanel value="other-orders" currentTab={selectedTab}>
            <OtherOrdersTab
              vendorId={vendorId}
              initialDateFilter={otherOrdersDateFilter}
              onDateFilterChange={handleOtherOrdersDateFilterChange}
            />
          </TabPanel>
        </Box>
      )}
      hideSidebar={true}
    />
  );
};

export default VendorInvoices;
